import React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { OysterLogo } from '@oysterjs/ui/logo';
import { Fieldset, FieldGroup, Field, Label, ErrorMessage } from '@oysterjs/ui/fieldset';
import { Input } from '@oysterjs/ui/input';
import { useSignInCompleteMutation, useSignInMutation } from '../api/graphql';
import { getValidationErrors } from '@oysterjs/core/graphql/errors';
import { PrimaryButton } from '@oysterjs/ui/button';
import { TransitionDiv } from '@oysterjs/ui/async';

const SignInPageContainer = (props: React.PropsWithChildren<{ title: string }>) => {
  return (
    <div className="bg-neutral-900 min-h-screen">
      <div className="relative isolate pt-12 sm:pt-24">
        <div
          aria-hidden="true"
          className="absolute inset-x-0 -top-10 -z-10 transform-gpu overflow-hidden blur-3xl"
        >
          <div
            style={{
              clipPath:
                'polygon(74.1% 44.1%, 100% 61.6%, 97.5% 26.9%, 85.5% 0.1%, 80.7% 2%, 72.5% 32.5%, 60.2% 62.4%, 52.4% 68.1%, 47.5% 58.3%, 45.2% 34.5%, 27.5% 76.7%, 0.1% 64.9%, 17.9% 100%, 27.6% 76.8%, 76.1% 97.7%, 74.1% 44.1%)'
            }}
            className="relative left-[calc(50%)] aspect-[1155/678] w-[36.125rem] -translate-x-1/2 rotate-[30deg] bg-gradient-to-tr from-primary-500 to-primary-500 opacity-30 sm:left-[calc(50%)] sm:w-[72.1875rem]"
          />
        </div>
      </div>

      <TransitionDiv
        transition={{ duration: 0.3, ease: 'easeInOut' }}
        className="isolate sm:bg-white sm:rounded-lg sm:shadow flex min-h-full max-w-md m-auto flex-1 flex-col justify-center px-6 py-12 lg:px-8"
      >
        <div className="sm:mx-auto sm:max-w-xs">
          <OysterLogo className="flex justify-center" />
          <h2 className="mt-10 text-center text-2xl/9 font-bold tracking-tight text-neutral-50 sm:text-neutral-900">
            {props.title}
          </h2>
        </div>
        {props.children}
      </TransitionDiv>
    </div>
  );
};

export const SignInPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = React.useState('');
  const [signIn, { loading, error }] = useSignInMutation();

  if (searchParams.get('email')) {
    return <SignInCompletePage />;
  }

  const validationErrors = getValidationErrors(error);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    signIn({ variables: { email } }).then((res) => {
      if (!res.errors?.length) {
        navigate(`/signin?email=${encodeURIComponent(email)}`);
      }
    });
  };

  return (
    <SignInPageContainer title="Sign in to your account">
      <div className="mt-10 sm:mx-auto sm:max-w-xs w-full">
        <form onSubmit={handleSubmit}>
          <Fieldset>
            <FieldGroup>
              <Field>
                <Label className="text-neutral-50 sm:text-neutral-900">Email address</Label>
                <Input
                  type="email"
                  required
                  autoComplete="email"
                  onChange={(e) => setEmail(e.currentTarget.value)}
                  invalid={validationErrors?.some((e) => e.field[0] === 'email')}
                />
                {validationErrors?.find((e) => e.field[0] === 'email')?.message && (
                  <ErrorMessage>
                    {validationErrors?.find((e) => e.field[0] === 'email')?.message}
                  </ErrorMessage>
                )}
              </Field>
            </FieldGroup>
          </Fieldset>

          <PrimaryButton type="submit" loading={loading}>
            Sign In
          </PrimaryButton>
          {validationErrors?.length === 0 && !!error?.message && (
            <div className="mt-4 text-sm/5 text-red-400 sm:text-red-600 sm:text-xs/4 dark:text-red-500">
              {error.message}
            </div>
          )}
        </form>

        <p className="mt-10 text-center text-sm text-neutral-400 sm:text-neutral-500">
          Can't sign in? Reach out to your agency for help.
        </p>
      </div>
    </SignInPageContainer>
  );
};

export const SignInCompletePage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [code, setCode] = React.useState(searchParams.get('code') || '');
  const [signInComplete, { loading, error }] = useSignInCompleteMutation();

  const email = searchParams.get('email') || '';

  const validationErrors = getValidationErrors(error);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    handleSignIn();
  };

  const handleSignIn = async () =>
    await signInComplete({ variables: { email, code } }).then((res) => {
      if (res.data?.accountUserSignIn?.accountUser?.id) {
        navigate('/');
      }
    });

  React.useEffect(() => {
    if (code.length === 6) {
      handleSignIn();
    }
  }, [code]);

  return (
    <SignInPageContainer title="Verify sign in request">
      <div className="mt-2 sm:mx-auto sm:max-w-xs w-full">
        <p className="text-center text-sm text-neutral-300 sm:text-neutral-700">
          We've sent a six-digit verification code to{' '}
          <strong className="font-semibold">{email}</strong>. Please enter the code below to
          continue.
        </p>
        <form onSubmit={handleSubmit} className="mt-4">
          <Fieldset>
            <FieldGroup>
              <Field>
                <Label className="text-neutral-50 sm:text-neutral-900">Verification code</Label>
                <Input
                  required
                  type="text"
                  placeholder="000000"
                  onChange={(e) => setCode(e.currentTarget.value)}
                  value={code}
                  invalid={validationErrors?.some((e) => e.field[0] === 'code')}
                />
                {validationErrors?.find((e) => e.field[0] === 'code')?.message && (
                  <ErrorMessage>
                    {validationErrors?.find((e) => e.field[0] === 'code')?.message}
                  </ErrorMessage>
                )}
              </Field>
            </FieldGroup>
          </Fieldset>

          <PrimaryButton loading={loading}>Sign In</PrimaryButton>
          {validationErrors?.length === 0 && !!error?.message && (
            <div className="mt-4 text-sm/5 text-red-400 sm:text-red-600 sm:text-xs/4 dark:text-red-500">
              {error.message}
            </div>
          )}
        </form>
      </div>
    </SignInPageContainer>
  );
};
